import { createAsyncThunk } from '@reduxjs/toolkit'
import { baseUrl, headersDefault } from '.'

// Fetch available nutritional goals
export const fetchNutritionalGoals = createAsyncThunk(
	'protocolSelection/fetchNutritionalGoals',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetch(`${baseUrl}/api/v1/nutritional-goal/list`, {
				headers: headersDefault(),
			})

			if (!response.ok) {
				throw new Error('Failed to fetch nutritional goals')
			}

			const data = await response.json()
			return data
		} catch (error) {
			return rejectWithValue(error.message)
		}
	}
)

// Save selected goals
export const saveSelectedGoals = createAsyncThunk(
	'protocolSelection/saveSelectedGoals',
	async (goals, { rejectWithValue }) => {
		try {
			const response = await fetch(`${baseUrl}/api/v1/master-club-nutritional-goals/`, {
				method: 'POST',
				headers: headersDefault(),
				body: JSON.stringify({ nutritional_goals: Array.isArray(goals) ? goals.filter(x => x != null).map(Number) : [] }),
			})

			if (!response.ok) {
				throw new Error('Failed to save selected goals')
			}

			const data = await response.json()
			return data
		} catch (error) {
			return rejectWithValue(error.message)
		}
	}
)

// Fetch compatible protocols
export const fetchCompatibleProtocols = createAsyncThunk(
	'protocolSelection/fetchCompatibleProtocols',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetch(`${baseUrl}/api/v1/master-club-nutritional-goals/list`, {
				headers: headersDefault(),
			})

			if (!response.ok) {
				throw new Error('Failed to fetch compatible protocols')
			}

			const data = await response.json()
			return data
		} catch (error) {
			return rejectWithValue(error.message)
		}
	}
)

// Fetch protocol timeline for a specific date
export const fetchProtocolTimelineByDate = createAsyncThunk(
	'protocolSelection/fetchProtocolTimelineByDate',
	async (date, { rejectWithValue }) => {
		try {
			const response = await fetch(
				`${baseUrl}/api/v1/master-club-protocol-timeline/${date}/fetch`,
				{ headers: headersDefault() },
			)

			if (!response.ok) {
				return rejectWithValue('Failed to fetch protocol timeline')
			}

			const data = await response.json()
			return data
		} catch (error) {
			return rejectWithValue(error.message)
		}
	},
)

// Full protocol timeline history for the current user
export const fetchProtocolTimelineList = createAsyncThunk(
	'protocolSelection/fetchProtocolTimelineList',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetch(
				`${baseUrl}/api/v1/master-club-protocol-timeline/list`,
				{ headers: headersDefault() },
			)

			if (!response.ok) {
				return rejectWithValue('Impossibile caricare lo storico percorsi.')
			}

			const data = await response.json()
			return Array.isArray(data) ? data : data?.data ?? []
		} catch (error) {
			return rejectWithValue(error.message)
		}
	},
)

// Upcoming AUTOMATIC percorso transition (fisso ends -> CRM-scheduled next), or null.
// Powers the "Il tuo percorso sta per cambiare" modal in the piano alimentare / lista spesa.
export const fetchUpcomingPercorsoTransition = createAsyncThunk(
	'protocolSelection/fetchUpcomingPercorsoTransition',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetch(
				`${baseUrl}/api/v1/master-club-protocol-timeline/upcoming-transition`,
				{ headers: headersDefault() },
			)

			if (!response.ok) {
				return rejectWithValue('Failed to fetch percorso transition')
			}

			const data = await response.json()
			return data?.data ?? null
		} catch (error) {
			return rejectWithValue(error.message)
		}
	},
)

/** Positive fixed_route_id from a protocol / timeline item, or null. */
export const resolveFixedRouteId = (source) => {
	const raw = source?.fixed_route_id ?? source?.route_id ?? null
	if (raw == null || raw === '') return null
	const id = Number(raw)
	return Number.isFinite(id) && id > 0 ? id : null
}

// Save protocol timeline (backend computes dates from start_type)
export const saveProtocolTimeline = createAsyncThunk(
	'protocolSelection/saveProtocolTimeline',
	async ({ protocolId, startType = 'now', fixedRouteId = null }, { rejectWithValue }) => {
		try {
			const start_type =
				startType === 'next_monday' ? 'next_monday' : 'now'
			const fixed_route_id =
				fixedRouteId != null
					? resolveFixedRouteId({ fixed_route_id: fixedRouteId })
					: null
			const body = {
				protocol_id: protocolId,
				start_type,
			}
			if (fixed_route_id != null) {
				body.fixed_route_id = fixed_route_id
			}
			const response = await fetch(`${baseUrl}/api/v1/master-club-protocol-timeline/store`, {
				method: 'POST',
				headers: headersDefault(),
				body: JSON.stringify(body),
			})

			if (!response.ok) {
				throw new Error('Failed to save protocol timeline')
			}

			const data = await response.json()
			return data
		} catch (error) {
			return rejectWithValue(error.message)
		}
	}
)

// Delete an explicit future timeline segment by id
export const deleteProtocolTimeline = createAsyncThunk(
	'protocolSelection/deleteProtocolTimeline',
	async (timelineId, { rejectWithValue }) => {
		try {
			const id = Number(timelineId)
			if (!Number.isFinite(id) || id <= 0) {
				return rejectWithValue('Timeline non valida.')
			}

			const response = await fetch(
				`${baseUrl}/api/v1/master-club-protocol-timeline/${id}`,
				{
					method: 'DELETE',
					headers: headersDefault(),
				},
			)

			if (!response.ok) {
				let message = 'Impossibile eliminare il percorso programmato.'
				try {
					const body = await response.json()
					if (typeof body?.message === 'string' && body.message) {
						message = body.message
					}
				} catch (_) {
					// keep default message
				}
				return rejectWithValue(message)
			}

			const data = await response.json().catch(() => ({ message: 'deleted' }))
			return { id, ...(data || {}) }
		} catch (error) {
			return rejectWithValue(error.message)
		}
	},
)
